import React, { useState } from "react";
import {
  DeleteOutlined,
  EditOutlined,
  HolderOutlined,
  PlusOutlined
} from "@ant-design/icons";
import { Button, Card, Form, Input, Popconfirm, Select, Space } from "antd";

const TYPE_OPTIONS = [
  { value: "data", label: "SQL-запрос" },
  { value: "view", label: "HTML-представление" }
];

const emptyItem = (type = "data") =>
  type === "data"
    ? { $type: "data", name: "", sql: "" }
    : { $type: "view", name: "", dataName: "", body: "" };

const ReportBodyEditor = ({ value = [], onChange, disabled = false }) => {
  const [editIndex, setEditIndex] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const startAdd = () => {
    setEditIndex(-1);
    setDraft(emptyItem());
  };

  const startEdit = (index) => {
    setEditIndex(index);
    setDraft({ ...value[index] });
  };

  const saveDraft = () => {
    const next =
      editIndex === -1
        ? [...value, draft]
        : value.map((item, index) => (index === editIndex ? draft : item));
    onChange?.(next);
    setEditIndex(null);
    setDraft(null);
  };

  const remove = (index) => onChange?.(value.filter((_, itemIndex) => itemIndex !== index));

  const drop = (index) => {
    if (dragIndex == null || dragIndex === index) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    const next = [...value];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    onChange?.(next);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const isData = draft?.$type === "data";
  const draftInvalid =
    !draft?.name?.trim() || (isData ? !draft?.sql?.trim() : !draft?.body?.trim());

  return (
    <div className="report-body-editor">
      {editIndex == null ? (
        <Button
          type="dashed"
          block
          icon={<PlusOutlined />}
          onClick={startAdd}
          disabled={disabled}
        >
          Добавить элемент
        </Button>
      ) : (
        <Card
          size="small"
          title={editIndex === -1 ? "Новый элемент" : "Редактирование элемента"}
          className="report-body-editor__form"
        >
          <Form layout="vertical">
            <Form.Item label="Тип">
              <Select
                value={draft.$type}
                options={TYPE_OPTIONS}
                disabled={editIndex !== -1}
                onChange={(type) => setDraft(emptyItem(type))}
              />
            </Form.Item>
            <Form.Item label="Наименование" required>
              <Input
                value={draft.name}
                maxLength={200}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </Form.Item>
            {isData ? (
              <Form.Item label="SQL-запрос" required>
                <Input.TextArea
                  value={draft.sql}
                  rows={9}
                  onChange={(event) => setDraft({ ...draft, sql: event.target.value })}
                />
              </Form.Item>
            ) : (
              <>
                <Form.Item label="Имя данных">
                  <Input
                    value={draft.dataName}
                    maxLength={200}
                    onChange={(event) =>
                      setDraft({ ...draft, dataName: event.target.value })
                    }
                  />
                </Form.Item>
                <Form.Item label="HTML" required>
                  <Input.TextArea
                    value={draft.body}
                    rows={12}
                    onChange={(event) => setDraft({ ...draft, body: event.target.value })}
                  />
                </Form.Item>
              </>
            )}
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <Button type="primary" disabled={draftInvalid} onClick={saveDraft}>
                Применить
              </Button>
              <Button
                onClick={() => {
                  setEditIndex(null);
                  setDraft(null);
                }}
              >
                Отмена
              </Button>
            </Space>
          </Form>
        </Card>
      )}

      <div className="report-body-editor__items">
        {value.map((item, index) => (
          <Card
            key={`${item.$type}-${index}`}
            size="small"
            draggable={!disabled && editIndex == null}
            className={[
              "report-body-editor__item",
              dragIndex === index ? "report-body-editor__item--dragging" : "",
              dragOverIndex === index ? "report-body-editor__item--over" : ""
            ]
              .filter(Boolean)
              .join(" ")}
            onDragStart={() => setDragIndex(index)}
            onDragOver={(event) => {
              event.preventDefault();
              if (dragIndex !== index) setDragOverIndex(index);
            }}
            onDragLeave={() => setDragOverIndex(null)}
            onDrop={(event) => {
              event.preventDefault();
              drop(index);
            }}
            onDragEnd={() => {
              setDragIndex(null);
              setDragOverIndex(null);
            }}
            title={
              <Space>
                <HolderOutlined />
                <span>{item.name || "(без названия)"}</span>
                <span className="report-body-editor__type">
                  ({TYPE_OPTIONS.find((type) => type.value === item.$type)?.label || item.$type})
                </span>
              </Space>
            }
            extra={
              !disabled && editIndex == null ? (
                <Space>
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => startEdit(index)}
                  />
                  <Popconfirm
                    title="Удалить элемент?"
                    okText="Удалить"
                    cancelText="Отмена"
                    onConfirm={() => remove(index)}
                  >
                    <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ) : null
            }
          />
        ))}
      </div>
      {!value.length && editIndex == null ? (
        <div className="report-body-editor__empty">Элементы не добавлены</div>
      ) : null}
    </div>
  );
};

export default ReportBodyEditor;
